import { type TextareaHTMLAttributes, forwardRef, useId } from 'react';
import styles from './Textarea.module.css';

export type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  label: string;
  hint?: string;
  error?: string | undefined;
};

/**
 * Textarea primitive — multi-line text input. Использует те же design-токены
 * что и Input (label, focus ring, hint/error slots). Минимум 3 rows.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, id, className, rows = 3, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  // FE-55 Sprint N+: visible required marker.
  const labelEndsWithAsterisk = typeof label === 'string' && label.trim().endsWith('*');
  const isRequired = Boolean(rest.required) && !labelEndsWithAsterisk;
  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label htmlFor={inputId} className={styles.label}>
        {label}
        {isRequired && (
          // F96 (гейт Ф6, круг 2, K1 UI-1): отметка обязательного поля — как у Select/Input
          // (одна разметка, один цвет); красная inline-звёздочка расходилась с соседним полем.
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div className={[styles.wrap, error ? styles.wrapError : ''].filter(Boolean).join(' ')}>
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          className={styles.textarea}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
